import { omit } from 'lodash/fp';
import { useCallback } from 'react';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom-v5-compat';
import { isPristine, isValid, reset, submit } from 'redux-form';

import useIsITEInternal from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsITEInternal';
import {
  INQUIRY_TEMPLATE_EDITOR_DRAFT_PLACEHOLDER_ID,
  INQUIRY_TEMPLATE_EDITOR_FORM_NAME,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import type {
  HandleSaveInquiryTemplateVersionProps,
  LoadValidationErrorsFormData,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { LegacyTemplateConfigFormData } from 'dashboard/forms/FormLegacyTemplate';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { FORM_INQUIRY_TEMPLATE_CONFIG } from 'dashboard/lib/constants/formNames';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { ModuleOptionKey } from 'dashboard/models/inquiry-template-module';
import { InquiryTemplateVersionStatus } from 'dashboard/models/inquiry-template-version';
import { Template } from 'dashboard/models/template';
import { VerificationTemplate } from 'dashboard/models/verification-template';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import { clearFormValues } from 'dashboard/redux/actions/inquiry-template';
import { showMessage } from 'dashboard/redux/actions/messages';
import { setVerificationTemplates } from 'dashboard/redux/actions/verification-templates';
import { isThemeEditorFormDirty } from 'dashboard/redux/selectors/theme';
import { getWorkflowActionMeta } from 'dashboard/redux/selectors/workflow-action-meta';
import { clearInquiryTemplate } from 'dashboard/redux/slices/inquiry-template';
import { clearInquiryTemplateModulePreviewMap } from 'dashboard/redux/slices/inquiry-template-modules';
import { RootState } from 'dashboard/redux/store';
import {
  discardDraftInquiryTemplateVersion,
  getEditorPath,
  initializeEditorHistory,
  initializeWorkflowStepContexts,
  loadComparedInquiryTemplateVersion,
  loadInquiryTemplateVersion,
  loadLegacyTemplate,
  loadStepLocalizedStrings,
  loadStepsPreview,
  loadValidationErrors,
  loadVersionDiff,
  publishInquiryTemplateVersion,
  redo,
  saveInquiryTemplateVersion,
  undo,
  updateInquiryTemplate,
  updateLegacyTemplate,
} from 'dashboard/redux/thunk-actions/inquiry-template';
import {
  loadInquiryTemplateModule,
  loadInquiryTemplateModuleOptions,
  loadInquiryTemplateModulePreview,
} from 'dashboard/redux/thunk-actions/inquiry-template-modules';
import { saveWorkflowVersion } from 'dashboard/redux/thunk-actions/inquiry-template-workflow';
import {
  addLocalDraftVersion,
  loadPartialInquiryTemplateVersions,
} from 'dashboard/redux/thunk-actions/inquiry-templates';
import { loadThemeList } from 'dashboard/redux/thunk-actions/organization';
import { loadUserList } from 'dashboard/redux/thunk-actions/user';
import { saveVtmplv } from 'dashboard/redux/thunk-actions/verification-template';
import DashboardInquiryTemplatesShowView from 'dashboard/views/DashboardInquiryTemplatesShowView';
import { previewInquiryFlow } from 'dashboard/views/DashboardInquiryTemplatesShowView/utils';
import DashboardLegacyTemplatesShowView from 'dashboard/views/DashboardLegacyTemplatesShowView';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';
import ApiResponseError, { ServerError } from 'lib/errors/ApiResponseError';
import { FieldSchema } from 'lib/fields';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useDebounced from 'lib/hooks/useDebounced';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import InquiryParams from 'verify/lib/interfaces/InquiryParams';

import 'lib/initializers/i18n';

const UPDATE_VALIDATION_ERRORS_DEBOUNCE_TIME = 1000;
const NETWORK_ERROR_MESSAGE_DEFAULT = 'We are experiencing network issues. Please try again.';

const getServerErrorMessage = (error: ServerError): string => {
  const { title, details } = error;
  return details ? `${title}: ${details.toString()}` : title;
};

type Params = {
  inquiryTemplateId: string;
  inquiryTemplateVersionId?: string;
};

const DashboardInquiryTemplatesShowRoute = (props: PropsFromRedux) => {
  const {
    clearInquiryTemplate,
    clearFormValues,
    currentEnvironment,
    inquiryTemplate,
    inquiryTemplateTreeData,
    partialInquiryTemplateVersions,
  } = props;
  const inquiryTemplateVersion = inquiryTemplateTreeData.inquiryTemplateVersion;
  useOnMountAndUnmount(() => {
    clearInquiryTemplate();
    clearFormValues();
  });

  const { inquiryTemplateId, inquiryTemplateVersionId } = useParams<Params>() as Params; // inquiryTemplateId will always be defined

  const hasLocalDraft = props.localDraftPartialInquiryTemplateVersion != null;
  const isLegacyTemplate = !inquiryTemplateId.startsWith('itmpl');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const submitForm = () => dispatch(submit(INQUIRY_TEMPLATE_EDITOR_FORM_NAME));
  const resetForm = () => dispatch(reset(INQUIRY_TEMPLATE_EDITOR_FORM_NAME));

  const isOmitLoadStepsPreviewOnSaveEnabled = useFeatureFlag(
    'dashboard:fle-omit-steps-preview-on-update',
  );

  useApiLoader({
    loader: async () => {
      await props.loadInquiryTemplateModuleOptions();
    },
    deps: [],
    skip: props.moduleOptions.length > 0,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to load template modules, please refresh the page.',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () => props.showMessage(NETWORK_ERROR_MESSAGE_DEFAULT, MessageType.Failure),
  });

  const { loading: loadingInquiryTemplate } = useApiLoader({
    loader: async () => {
      dispatch(clearInquiryTemplateModulePreviewMap());
      if (isLegacyTemplate) {
        await props.loadLegacyTemplate(inquiryTemplateId);
      } else {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        await props.loadInquiryTemplateVersion(inquiryTemplateVersionId);
        await props.initializeWorkflowStepContexts(inquiryTemplateId);
      }
    },
    deps: [],
    // Don't load over an existing local draft nor load when we are creating a new draft
    skip:
      hasLocalDraft || inquiryTemplateVersionId === INQUIRY_TEMPLATE_EDITOR_DRAFT_PLACEHOLDER_ID,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load template, please refresh the page.', MessageType.Failure);
      }
    },
    onNetworkError: () => props.showMessage(NETWORK_ERROR_MESSAGE_DEFAULT, MessageType.Failure),
  });

  const {
    callback: handleLoadPreviewWithLocale,
    loading: loadingPreviewWithLocale,
    error: handleLoadPreviewWithLocaleError,
  } = useApiCallback(
    async (locale: InquiryIso6391LanguageCode) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      await props.loadStepsPreview(inquiryTemplateVersionId, locale);
    },
    [inquiryTemplateVersionId],
  );

  useApiErrorHandler({
    error: handleLoadPreviewWithLocaleError,
    onError: (err) => {
      if (isPermissionsError(err)) {
        props.showMessage(permissionsErrorMessage(err), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to reload template preview, please try again.',
          MessageType.Failure,
        );
      }
      handleInternalServerErrorDisplay(err);
    },
    onNetworkError: () => props.showMessage(NETWORK_ERROR_MESSAGE_DEFAULT, MessageType.Failure),
  });

  const {
    callback: handleSelectVersion,
    loading: loadingSelectedVersion,
    error: loadingSelectedVersionError,
  } = useApiCallback(async (inquiryTemplateVersionId: string) => {
    await dispatch(loadInquiryTemplateVersion(inquiryTemplateVersionId));
    navigate(getEditorPath(inquiryTemplateId, inquiryTemplateVersionId, 'flow'), { replace: true });
  }, []);

  useApiErrorHandler({
    error: loadingSelectedVersionError,
    onError: (err) => {
      if (isPermissionsError(err)) {
        props.showMessage(permissionsErrorMessage(err), MessageType.Failure);
      } else {
        props.showMessage('Failed to the selected version, please try again.', MessageType.Failure);
      }
      handleInternalServerErrorDisplay(err);
    },
    onNetworkError: () => props.showMessage(NETWORK_ERROR_MESSAGE_DEFAULT, MessageType.Failure),
  });

  const { loading: loadingLegacyTemplateData } = useApiLoader({
    loader: async () => {
      await Promise.all([props.loadThemeList(), props.loadUserList()]);
    },
    deps: [],
    skip: !isLegacyTemplate,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to load template data, please refresh the page.',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () => props.showMessage(NETWORK_ERROR_MESSAGE_DEFAULT, MessageType.Failure),
  });

  const handleInquiryPreview = useCallback(() => {
    const params: InquiryParams = isLegacyTemplate
      ? {
          'environment-id': currentEnvironment.id,
          'template-id': inquiryTemplateId,
        }
      : {
          'environment-id': currentEnvironment.id,
          'inquiry-template-id': inquiryTemplateId,
          'inquiry-template-version-id': inquiryTemplateVersionId,
        };
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    previewInquiryFlow(params, location, props.organization.attributes.subdomain);
  }, [
    currentEnvironment.id,
    inquiryTemplateId,
    inquiryTemplateVersionId,
    isLegacyTemplate,
    props.organization.attributes.subdomain,
  ]);

  const {
    callback: handleSaveLegacyTemplate,
    loading: saveLegacyTemplateLoading,
    error: saveLegacyTemplateError,
  } = useApiCallback(
    async (formValues: LegacyTemplateConfigFormData) => {
      await props.updateLegacyTemplate(
        formValues as LegacyTemplateConfigFormData,
        inquiryTemplateId,
      );

      props.showMessage('Your changes were successfully saved.', MessageType.Success);
    },
    [inquiryTemplateId],
  );

  useApiErrorHandler({
    error: saveLegacyTemplateError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to save template, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () => props.showMessage(NETWORK_ERROR_MESSAGE_DEFAULT, MessageType.Failure),
  });

  const {
    callback: handleTestSupportFormEmail,
    loading: isSendingTestSupportEmail,
    error: testSupportEmailError,
  } = useApiCallback(async () => {
    await SessionClientDashboard.postResourceAction(
      {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        id: inquiryTemplate.id,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        type: inquiryTemplate.type,
      },
      'send-test-support-ticket',
    );
    props.showMessage('Sent test support email', MessageType.Success);
  }, [inquiryTemplate]);

  useApiErrorHandler({
    error: testSupportEmailError,
    onNetworkError: () =>
      props.showMessage('Failed to send test email. Please try again.', MessageType.Failure),
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to send test email. Please try again.', MessageType.Failure);
      }
    },
  });

  const {
    callback: handleSaveInquiryTemplateVersion,
    loading: saveInquiryTemplateVersionLoading,
    error: saveInquiryTemplateVersionError,
  } = useApiCallback(
    async ({
      formValues,
      inquiryTemplate,
      lockVersion,
      reloadPath,
    }: HandleSaveInquiryTemplateVersionProps) => {
      const currentInquiryTemplate = inquiryTemplate as InquiryTemplate;
      if (
        !window.Cypress &&
        inquiryTemplateVersion.attributes.status === InquiryTemplateVersionStatus.Published &&
        currentInquiryTemplate?.relationships['latest-draft-version']?.data != null
      ) {
        props.showMessage(
          'A draft already exists. Click the link in the banner to continue making edits.',
          MessageType.Failure,
        );

        return;
      }

      await props.saveInquiryTemplateVersion(
        inquiryTemplateId,
        inquiryTemplateVersion,
        // vtv and wfv are updated in different paths. Keep response sizes small.
        omit(['verificationTemplateVersions', 'workflowVersions', 'template-name'], formValues),
        lockVersion,
        reloadPath,
        isOmitLoadStepsPreviewOnSaveEnabled,
        navigate,
      );

      // Reset only on successful save, otherwise keep dirty state to display validation errors and preserve progress
      // FIXME (INQ-59): Should reset only the form fields pertaining to inquiry template version, as this can incorrectly
      // reset the template name form value if the template update (which occurs after this request) fails
      resetForm();

      // display message as 'updated' since it's functionally the same as 'created and updated'
      props.showMessage('Your draft was successfully updated.', MessageType.Success);
    },
    [inquiryTemplate, inquiryTemplateId, inquiryTemplateVersion, inquiryTemplateVersionId],
  );

  const {
    callback: handleSaveInquiryTemplate,
    loading: saveInquiryTemplateLoading,
    error: saveInquiryTemplateError,
  } = useApiCallback(
    async ({ formValues }: HandleSaveInquiryTemplateVersionProps) => {
      await props.updateInquiryTemplate(inquiryTemplateId, {
        name: formValues['template-name'],
        description: formValues['template-description'],
      });
      props.showMessage('Your template was successfully updated.', MessageType.Success);
    },
    [inquiryTemplate, inquiryTemplateId, inquiryTemplateVersion, inquiryTemplateVersionId],
  );

  const {
    callback: handleUpdateVerificationTemplateVersions,
    loading: updateVerificationTemplateVersionsLoading,
    error: updateVerificationTemplateVersionsError,
  } = useApiCallback(async (verificationTemplateVersions: VerificationTemplateVersion[]) => {
    const verificationTemplateVersionUpdates = verificationTemplateVersions.map((vtmplVersion) =>
      props.saveVtmplv(vtmplVersion),
    );
    return await Promise.all(verificationTemplateVersionUpdates);
    // omit update success banner message as this is always called in conjunction with a draft update or create
  }, []);

  const {
    callback: handleUpdateWorkflowVersions,
    loading: updateWorkflowVersionsLoading,
    error: updateWorkflowVersionsError,
  } = useApiCallback(
    async (workflowVersions: WorkflowVersion[]) => {
      const workflowVersionMap = props.inquiryTemplateTreeData.schema.workflowVersions;
      const workflowVersionUpdates = workflowVersions.map((workflowVersion) =>
        props.saveWorkflowVersion({
          workflowVersion,
          withWorkflowNameUpdate:
            // optional access because we can add net-new workflow versions in FLE
            workflowVersionMap[workflowVersion.id]?.attributes?.['workflow-name'] !==
            workflowVersion.attributes['workflow-name'],
          environmentId: currentEnvironment.id,
          inquiryTemplateId: inquiryTemplateId,
        }),
      );
      return await Promise.all(workflowVersionUpdates);
      // omit update success banner message as this is always called in conjunction with a draft update or create
    },
    [
      currentEnvironment.id,
      inquiryTemplateId,
      props.inquiryTemplateTreeData.schema.workflowVersions,
    ],
  );

  const { callback: handleLoadModulePreview, error: handleLoadModulePreviewError } = useApiCallback(
    async (moduleKey: ModuleOptionKey) => {
      await props.loadInquiryTemplateModulePreview(moduleKey, inquiryTemplateVersionId);
    },
    [inquiryTemplateVersionId],
  );

  const { callback: handleLoadModule, error: handleLoadModuleError } = useApiCallback(
    async (moduleKey: ModuleOptionKey, currentFieldSchema: FieldSchema[]) => {
      return await props.loadInquiryTemplateModule(
        moduleKey,
        currentFieldSchema,
        inquiryTemplateVersionId,
      );
    },
    [inquiryTemplateVersionId],
  );

  const { callback: handleLoadStepLocalizedStrings } = useApiCallback(
    async (stepNames: Array<string>, locales: Array<InquiryIso6391LanguageCode>) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      await props.loadStepLocalizedStrings(inquiryTemplateVersionId, stepNames, locales);
    },
    [inquiryTemplateVersionId],
  );

  const handleAddLocalDraftVersion = useCallback(async () => {
    const { versionId } = await props.addLocalDraftVersion(
      inquiryTemplateVersion,
      partialInquiryTemplateVersions,
    );
    navigate(getEditorPath(inquiryTemplateId, versionId, 'flow'));
  }, [
    inquiryTemplateId,
    inquiryTemplateVersion,
    partialInquiryTemplateVersions,
    addLocalDraftVersion,
    navigate,
  ]);

  const handleUpdateStoredVerificationTemplates = useCallback(
    (moduleVerificationTemplates: VerificationTemplate[]) =>
      props.setVerificationTemplates(
        [...props.verificationTemplates, ...moduleVerificationTemplates],
        {
          'current-page': 1,
          'total-count': props.verificationTemplates.length + moduleVerificationTemplates.length,
          'total-pages': 1,
        },
      ),
    [],
  );

  useApiErrorHandler({
    error: handleLoadModulePreviewError || handleLoadModuleError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load module preview, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () => props.showMessage(NETWORK_ERROR_MESSAGE_DEFAULT, MessageType.Failure),
  });

  const shouldDisplayServerErrors = useIsITEInternal();
  const handleInternalServerErrorDisplay = (err: ApiResponseError) => {
    if (shouldDisplayServerErrors) {
      err.errors?.forEach((serverError) => {
        props.showMessage(getServerErrorMessage(serverError), MessageType.Notice);
      });
    }
  };

  useApiErrorHandler({
    error:
      saveInquiryTemplateVersionError ||
      updateVerificationTemplateVersionsError ||
      updateWorkflowVersionsError,
    onError: (err) => {
      if (isPermissionsError(err)) {
        props.showMessage(permissionsErrorMessage(err), MessageType.Failure);
      } else if (err.status === 422 && err.errors?.[0].code === 'multiple_draft_error') {
        props.showMessage(
          'A draft already exists on this template. Please reload the page and try again.',
          MessageType.Failure,
        );
      } else {
        props.showMessage('Failed to update draft, please try again.', MessageType.Failure);
      }
      handleInternalServerErrorDisplay(err);
    },
    onNetworkError: () => props.showMessage(NETWORK_ERROR_MESSAGE_DEFAULT, MessageType.Failure),
  });

  useApiErrorHandler({
    error: saveInquiryTemplateError,
    onError: (err) => {
      if (isPermissionsError(err)) {
        props.showMessage(permissionsErrorMessage(err), MessageType.Failure);
      } else {
        props.showMessage('Failed to update template, please try again.', MessageType.Failure);
      }
      handleInternalServerErrorDisplay(err);
    },
    onNetworkError: () => props.showMessage(NETWORK_ERROR_MESSAGE_DEFAULT, MessageType.Failure),
  });

  const {
    callback: handleDiscardInquiryTemplateVersion,
    loading: isDiscarding,
    error: discardInquiryTemplateVersionError,
  } = useApiCallback(
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types
    async (inquiryTemplateId: string, reloadCurrentVersion: boolean = true) => {
      await props.discardDraftInquiryTemplateVersion({
        inquiryTemplateId,
        reloadCurrentVersion,
        navigate,
      });
      props.showMessage('Draft was successfully discarded.', MessageType.Success);
    },
    [],
  );

  useApiErrorHandler({
    error: discardInquiryTemplateVersionError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to discard draft, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () => props.showMessage(NETWORK_ERROR_MESSAGE_DEFAULT, MessageType.Failure),
  });

  /**
   * Fetches another template version to compare with the one currently viewed in ITE.
   * Currently the usage is only to review change diffs between the to-be published
   * version and the current published version on-publish, but a more polished
   * version of this feature could be used as a general-purpose comparison tool
   */
  const {
    callback: handleLoadComparedVersion,
    loading: loadingComparedVersion,
    error: loadComparedVersionError,
  } = useApiCallback(async () => {
    const publishedVersionId = (inquiryTemplate as InquiryTemplate)?.relationships?.[
      'latest-published-version'
    ]?.data?.id;
    if (publishedVersionId) {
      await props.loadComparedInquiryTemplateVersion(publishedVersionId);
    } else {
      props.showMessage(
        'Unable to load changes since last published version.',
        MessageType.Failure,
      );
    }
  }, [inquiryTemplate]);

  useApiErrorHandler({
    error: loadComparedVersionError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Unable to load changes since last published version.',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () => props.showMessage(NETWORK_ERROR_MESSAGE_DEFAULT, MessageType.Failure),
  });

  const {
    callback: handleLoadVersionDiff,
    loading: loadingVersionDiff,
    error: loadVersionDiffError,
  } = useApiCallback(async () => {
    const publishedVersionId = (inquiryTemplate as InquiryTemplate)?.relationships?.[
      'latest-published-version'
    ]?.data?.id;
    if (publishedVersionId) {
      await props.loadVersionDiff(inquiryTemplateId);
    } else {
      props.showMessage(
        'Unable to load changes since last published version.',
        MessageType.Failure,
      );
    }
  }, [inquiryTemplate]);

  useApiErrorHandler({
    error: loadVersionDiffError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Unable to load changes since last published version.',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () => props.showMessage(NETWORK_ERROR_MESSAGE_DEFAULT, MessageType.Failure),
  });

  const { callback: handleUpdateValidationErrors, error: updateValidationErrorsFailure } =
    useApiCallback(
      async (inquiryTemplateVersionId: string, formData: LoadValidationErrorsFormData) => {
        await props.loadValidationErrors(inquiryTemplateVersionId, formData);
      },
      [],
    );

  const [handleDebouncedUpdateValidationErrors] = useDebounced(
    handleUpdateValidationErrors,
    UPDATE_VALIDATION_ERRORS_DEBOUNCE_TIME,
  );

  useApiErrorHandler({
    error: updateValidationErrorsFailure,
    onError: (err) => {
      if (isPermissionsError(err)) {
        props.showMessage(permissionsErrorMessage(err), MessageType.Failure);
      } else {
        props.showMessage('Failed to update validation errors.', MessageType.Failure);
      }
      handleInternalServerErrorDisplay(err);
    },
    onNetworkError: () => props.showMessage(NETWORK_ERROR_MESSAGE_DEFAULT, MessageType.Failure),
  });

  const {
    callback: handlePublishInquiryTemplateVersion,
    loading: isPublishing,
    error: publishInquiryTemplateVersionError,
  } = useApiCallback(
    async (shouldUpdateEnhancedBilling: boolean, lockVersion: number) => {
      await props.publishInquiryTemplateVersion(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        inquiryTemplateVersionId,
        shouldUpdateEnhancedBilling,
        lockVersion,
      );
      props.showMessage('Your draft was successfully published.', MessageType.Success);
    },
    [inquiryTemplateVersionId],
  );

  useApiErrorHandler({
    error: publishInquiryTemplateVersionError,
    onError: (err) => {
      if (isPermissionsError(err)) {
        props.showMessage(permissionsErrorMessage(err), MessageType.Failure);
      } else {
        if (err.status === 422 && err.errors?.[0].code === 'failed_auto_migration_error') {
          // reload ITV to refresh auto-migration status
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          props.loadInquiryTemplateVersion(inquiryTemplateVersionId);
        }
        props.showMessage(
          'Failed to publish template draft, please try again.',
          MessageType.Failure,
        );
      }
      handleInternalServerErrorDisplay(err);
    },
    onNetworkError: () => props.showMessage(NETWORK_ERROR_MESSAGE_DEFAULT, MessageType.Failure),
  });

  const {
    callback: handleLoadPartialInquiryTemplateVersions,
    error: handleLoadPartialInquiryTemplateVersionsError,
    loading: isLoadingPartialInquiryTemplateVersions,
  } = useApiCallback(async () => {
    props.loadPartialInquiryTemplateVersions(inquiryTemplateId);
  }, [inquiryTemplateId]);

  useApiErrorHandler({
    error: handleLoadPartialInquiryTemplateVersionsError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load versions, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () => props.showMessage(NETWORK_ERROR_MESSAGE_DEFAULT, MessageType.Failure),
  });

  const isLoadingLegacy = loadingInquiryTemplate || loadingLegacyTemplateData;
  const isLoading = loadingInquiryTemplate || loadingSelectedVersion || loadingPreviewWithLocale;

  const isUpdating =
    saveLegacyTemplateLoading ||
    saveInquiryTemplateVersionLoading ||
    saveInquiryTemplateLoading ||
    updateVerificationTemplateVersionsLoading ||
    updateWorkflowVersionsLoading;

  return isLegacyTemplate ? (
    <DashboardLegacyTemplatesShowView
      template={props.inquiryTemplate as Template}
      themes={props.themes}
      currentEnvironmentType={props.currentEnvironment.type}
      users={props.users}
      isLoading={isLoadingLegacy}
      isUpdating={isUpdating}
      isSendingTestSupportEmail={isSendingTestSupportEmail}
      isFormPristine={props.isFormPristine}
      isFormValid={props.isFormValid}
      onInquiryPreview={handleInquiryPreview}
      onSaveLegacyTemplate={handleSaveLegacyTemplate}
      onTestSupportFormEmail={handleTestSupportFormEmail}
      submit={props.submit}
    />
  ) : (
    <DashboardInquiryTemplatesShowView
      inquiryTemplateTreeData={props.inquiryTemplateTreeData}
      stepsPreview={props.stepsPreview}
      inquiryTemplate={props.inquiryTemplate as InquiryTemplate}
      versionDiff={props.versionDiff}
      currentEnvironmentType={props.currentEnvironment.type}
      organization={props.organization}
      comparedPublishedVersion={props.comparedPublishedVersion}
      partialInquiryTemplateVersions={props.partialInquiryTemplateVersions}
      localPartialInquiryTemplateVersion={props.localDraftPartialInquiryTemplateVersion}
      restoredFrom={props.restoredFrom}
      locale={props.locale}
      comparedSchema={props.comparedSchema}
      moduleOptions={props.moduleOptions}
      modulePreviewMap={props.modulePreviewMap}
      pendingStates={{
        isDiscarding,
        isLoading,
        isPublishing,
        isUpdating,
      }}
      loadingComparedVersion={loadingComparedVersion}
      loadingVersionDiff={loadingVersionDiff}
      undoButtonEnabled={props.undoButtonEnabled || hasLocalDraft}
      redoButtonEnabled={props.redoButtonEnabled}
      isFormPristine={props.isFormPristine}
      isFormValid={props.isFormValid}
      validationErrors={props.validationErrors}
      inputStep={props.inputStep}
      loadingWorkflowSchema={loadingInquiryTemplate}
      handleAddLocalDraftVersion={handleAddLocalDraftVersion}
      handleSelectVersion={handleSelectVersion}
      handleSaveInquiryTemplateVersion={handleSaveInquiryTemplateVersion}
      handleLoadPartialInquiryTemplateVersions={handleLoadPartialInquiryTemplateVersions}
      isLoadingPartialInquiryTemplateVersions={isLoadingPartialInquiryTemplateVersions}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleSaveInquiryTemplate={handleSaveInquiryTemplate}
      handleDebouncedUpdateValidationErrors={handleDebouncedUpdateValidationErrors}
      handleLoadComparedVersion={handleLoadComparedVersion}
      handleLoadVersionDiff={handleLoadVersionDiff}
      handlePublishDraft={handlePublishInquiryTemplateVersion}
      handleDiscardDraft={handleDiscardInquiryTemplateVersion}
      handleUpdateStoredVerificationTemplates={handleUpdateStoredVerificationTemplates}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleUpdateVerificationTemplateVersions={handleUpdateVerificationTemplateVersions}
      handleUpdateWorkflowVersions={handleUpdateWorkflowVersions}
      handleSetLocale={handleLoadPreviewWithLocale}
      handleLoadModulePreview={handleLoadModulePreview}
      handleLoadModule={handleLoadModule}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleLoadStepLocalizedStrings={handleLoadStepLocalizedStrings}
      handleHistoryInitialization={props.initializeEditorHistory}
      handleUndo={props.undo}
      handleRedo={props.redo}
      resetForm={resetForm}
      submit={submitForm}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  inputStep: state.workflow.present.inputStep,
  actionMeta: getWorkflowActionMeta(state),
  inquiryTemplateTreeData: state.inquiryTemplate.inquiryTemplateTreeData,
  stepsPreview: state.inquiryTemplate.stepsPreview,
  currentEnvironment: state.currentEnvironment.currentEnvironment,
  inquiryTemplate: state.inquiryTemplate.inquiryTemplate,
  versionDiff: state.inquiryTemplate.versionDiff,
  comparedPublishedVersion: state.inquiryTemplate.comparedVersion,
  partialInquiryTemplateVersions: state.inquiryTemplates.partialInquiryTemplateVersions,
  localDraftPartialInquiryTemplateVersion:
    state.inquiryTemplate.localDraftPartialInquiryTemplateVersion,
  restoredFrom: state.inquiryTemplate.restoredFrom,
  isFormPristine:
    isPristine(FORM_INQUIRY_TEMPLATE_CONFIG)(state) &&
    isPristine(INQUIRY_TEMPLATE_EDITOR_FORM_NAME)(state) &&
    !isThemeEditorFormDirty(state),
  isFormValid:
    isValid(FORM_INQUIRY_TEMPLATE_CONFIG)(state) &&
    isValid(INQUIRY_TEMPLATE_EDITOR_FORM_NAME)(state),
  validationErrors: state.inquiryTemplate.validationErrors,
  locale: state.inquiryTemplate.locale,
  moduleOptions: state.inquiryTemplateModules.moduleOptions,
  modulePreviewMap: state.inquiryTemplateModules.modulePreviewMap,
  organization: state.organization.organization,
  comparedSchema: state.inquiryTemplate.comparedSchema,
  themes: state.organization.themes,
  undoButtonEnabled: state.inquiryTemplate.editorHistory.historyIndex > 0,
  redoButtonEnabled:
    state.inquiryTemplate.editorHistory.historyIndex !==
    state.inquiryTemplate.editorHistory.items.length - 1,
  users: state.user.users,
  verificationTemplates: state.verificationTemplates.verificationTemplates,
});

const mapDispatchToProps = {
  loadLegacyTemplate,
  updateLegacyTemplate,
  loadInquiryTemplateVersion,
  initializeWorkflowStepContexts,
  loadComparedInquiryTemplateVersion,
  loadPartialInquiryTemplateVersions,
  loadStepsPreview,
  loadVersionDiff,
  loadInquiryTemplateModuleOptions,
  loadInquiryTemplateModulePreview,
  loadInquiryTemplateModule,
  loadStepLocalizedStrings,
  loadValidationErrors,
  saveInquiryTemplateVersion,
  publishInquiryTemplateVersion,
  discardDraftInquiryTemplateVersion,
  updateInquiryTemplate,
  addLocalDraftVersion,
  setVerificationTemplates,
  clearFormValues,
  clearInquiryTemplate,
  saveVtmplv,
  saveWorkflowVersion,
  loadThemeList,
  loadUserList,
  showMessage,
  initializeEditorHistory,
  undo,
  redo,
  reset: () => reset(INQUIRY_TEMPLATE_EDITOR_FORM_NAME),
  submit: () => submit(FORM_INQUIRY_TEMPLATE_CONFIG),
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardInquiryTemplatesShowRoute);
